/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */

import {useState, useEffect, useContext} from 'react';
import './FinishScreen.scss';
import {ExdsGrid, ExdsLayout, ExdsLink, ExdsSpacing, ExdsText} from '@exds/core';
import FinishSteps from './FinishSteps';
import AppContext from '../../AppContext';
import {STEP_PERFORMED, WORKSPACE_LINK_EXTERNAL, WORKSPACE_LINK_EXTERNAL_NO_SLO} from '../../constants/constants';
import {addPageLoadEvent} from "../AdobeAnalytics/AdobeAnalytics";

export default function FinishScreen() {
  const {
    sidContext,
    hashContext,
    external,
    phoneNumberContext,
    transactionIdContext,
    hashStepsStatusContext,
    expirationTimeContext,
    loadAA,
    isSoftTokenEnabledContext,
    isSmartLogonEnabledContext
  } = useContext(AppContext);
  const [showTitleText, setShowTitleText] = useState(false);
  const [showBodyText, setShowBodyText] = useState(false);
  const workspaceLink = isSmartLogonEnabledContext.at(0) === "true" ? WORKSPACE_LINK_EXTERNAL : WORKSPACE_LINK_EXTERNAL_NO_SLO;
  const sid = sidContext.at(0);
  const hash = hashContext.at(0);
  const phoneNumber = phoneNumberContext.at(0);
  const transactionId = transactionIdContext.at(0);
  const hashStepsStatus = hashStepsStatusContext.at(0);
  const expirationTime = expirationTimeContext.at(0);
  const isSoftTokenEnabledBool = isSoftTokenEnabledContext.at(0) === "true";
  const isSmartLogonEnabledBool = isSmartLogonEnabledContext.at(0) === "true";

  useEffect(() => {
    if(loadAA) {
      const pageName = 'Final Page';
      addPageLoadEvent(pageName, sid);
    }
  }, [loadAA, sid]);

  useEffect(() => {
    if (!isSoftTokenEnabledBool && !isSmartLogonEnabledBool) {
      return;
    }
    const publishEvents = async () => {
      const events = [
        hashStepsStatus?.rsaSetup !== STEP_PERFORMED && 'SKIP_RSA',
        hashStepsStatus?.sloSetup !== STEP_PERFORMED && 'SKIP_SLO'
      ].filter(Boolean);

      if(events.length > 0) {
        await fetch('/api/publishEvents', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            sid,
            phoneNumber,
            transactionId,
            rsaSetup: hashStepsStatus.rsaSetup,
            sloSetup: hashStepsStatus.sloSetup,
            sloTest: hashStepsStatus.sloTest,
            expirationTime,
            events,
            hash
          }),
        });
      }
    };

    publishEvents();
  }, [isSoftTokenEnabledBool, isSmartLogonEnabledBool]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const titleTextTimeout = setTimeout(() => {
      setShowTitleText(true);
    }, 1000);

    const bodyTextTimeout = setTimeout(() => {
      setShowBodyText(true);
    }, 2500);

    return () => {
      clearTimeout(titleTextTimeout);
      clearTimeout(bodyTextTimeout);
    };
  },[]);

  return (
    <>
      <ExdsLayout display="flex" flexDirection="column" alignItems="center">
        <ExdsGrid container justifyContent="center" className="finish-container">
          <ExdsLayout alignItems="center" display="flex" flexDirection="column" gap="1.5rem">
            <div className="finish-screen">
              <div className={`finish-text ${showTitleText ? 'fade-in' : ''}`}>
                <ExdsText primitive="p" textStyle="headlineSmall" alignment="center" className="style-title">
                  You're all set!
                </ExdsText>
                <ExdsLayout display="flex" flexDirection="row" justifyContent="center" className="style-steps">
                  <FinishSteps stepsCompleted={hashStepsStatus} />
                </ExdsLayout>
                {external && (
                  <>
                    <div className="finish-screen-vector">
                      <img src="../../assets/images/Vector15.svg" alt="Vector"/>
                    </div>
                    <ExdsText primitive="span" alignment="center" className="remote-text">
                      Visit <ExdsText primitive="span"> <ExdsLink href={`https://${workspaceLink}`} target="_blank">{workspaceLink}</ExdsLink> to access your remote desktop.</ExdsText>
                    </ExdsText>
                  </>
                )}
              </div>

              <div className={`finish-text ${showBodyText ? 'fade-in' : ''}`}>
                <ExdsText primitive="p" textStyle="headlineLarge" alignment="center">
                  Enjoy your first day!
                </ExdsText>
                <ExdsSpacing padding="115px 80px 80px">
                  {!external && (
                    <>
                  <ExdsText primitive="p" textStyle="bodyLarge" alignment="center">
                    Once you've logged in, type <strong>go/WelcomeNewHire</strong> into your<br/>
                    browser for more help with setting up your technology.
                  </ExdsText>
                    </>
                  )}
                  {external && (
                    <>
                  <ExdsText primitive="p" textStyle="bodyLarge" alignment="center">
                    For more help setting up your technology, after login<br/> type <strong>go/WelcomeNewHire </strong>
                    in a new browser window <br/> within Workspace.
                  </ExdsText>
                    </>
                  )}
                <ExdsLayout display="flex" flexDirection="row" justifyContent="center">
                  <img src="../../assets/images/JPMC_Logo.svg" alt="JPMC Logo"/>
                </ExdsLayout>
                </ExdsSpacing>
              </div>
            </div>
          </ExdsLayout>
        </ExdsGrid>
      </ExdsLayout>
    </>
  );
}


